<script lang="ts">
    import Icon from "@components/material/Icon.svelte"
    import type { TabClass } from "@store/pageTracker"
    import { createEventDispatcher, onMount, tick } from "svelte"
    import MobileReadingTabStripItem from "./MobileReadingTabStripItem.svelte"

    export let tabList: TabClass[] = []
    export let selectedTabID = "abandoned"
    export let createTab: () => void = () => {}
    export let browseWithoutTab: () => void = () => {}

    const dispatch = createEventDispatcher<{
        select: void
    }>()

    let scroller: HTMLDivElement
    let atStart = true
    let atEnd = true
    let resizeObserver: ResizeObserver | null = null

    $: tabFingerprint = tabList.map((tab) => tab.id).join("|")
    $: if (scroller || tabFingerprint) void refreshScrollState()

    onMount(() => {
        resizeObserver = new ResizeObserver(() => processScroll())
        if (scroller) resizeObserver.observe(scroller)

        processScroll()

        return () => {
            resizeObserver?.disconnect()
        }
    })

    async function refreshScrollState() {
        await tick()
        processScroll()
    }

    function processScroll() {
        if (!scroller) return

        const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth)

        atStart = scroller.scrollLeft <= 2
        atEnd = scroller.scrollLeft >= maxScroll - 2
    }

    function scrollTabs(direction: "left" | "right") {
        if (!scroller) return

        scroller.scrollBy({
            left: ((direction === "right" ? 1 : -1) * scroller.clientWidth) / 1.5,
            behavior: "smooth",
        })

        window.requestAnimationFrame(processScroll)
        window.setTimeout(processScroll, 320)
    }

    function selectBrowse() {
        browseWithoutTab()
        dispatch("select")
    }

    function selectTab() {
        dispatch("select")
    }
</script>

<div class="mobile-tab-strip" aria-label="Reading tabs">
    <button
        type="button"
        class="scroll-arrow left"
        class:hidden={atStart}
        aria-label="Scroll reading tabs left"
        tabindex={atStart ? -1 : 0}
        on:click={() => scrollTabs("left")}
    >
        <Icon icon="arrow" />
    </button>

    <div
        bind:this={scroller}
        class="mobile-tab-scroller"
        role="list"
        aria-label="Open reading tabs"
        on:scroll={processScroll}
    >
        <div role="listitem" class="tab-list-item">
            <button
                type="button"
                class="utility-tab browse-tab"
                class:selected={selectedTabID === "abandoned"}
                aria-current={selectedTabID === "abandoned" ? "page" : undefined}
                aria-label="Browse the table of contents without using a reading tab"
                title="Browse"
                on:click={selectBrowse}
            >
                <Icon icon="table-of-contents" />
                <span>Browse</span>
            </button>
        </div>

        {#each tabList as tab (tab.id)}
            <div role="listitem" class="tab-list-item">
                <MobileReadingTabStripItem
                    {tab}
                    selected={tab.id === selectedTabID}
                    on:select={selectTab}
                    on:scrollstatechange={processScroll}
                />
            </div>
        {/each}

        <div role="listitem" class="tab-list-item">
            <button
                type="button"
                class="utility-tab add-tab"
                aria-label="Create new reading tab"
                title="New tab"
                on:click={createTab}
            >
                <Icon icon="plus" />
                <span>New</span>
            </button>
        </div>
    </div>

    <button
        type="button"
        class="scroll-arrow right"
        class:hidden={atEnd}
        aria-label="Scroll reading tabs right"
        tabindex={atEnd ? -1 : 0}
        on:click={() => scrollTabs("right")}
    >
        <Icon icon="arrow-right" />
    </button>
</div>

<style>
    .mobile-tab-strip {
        --scroll-fade-width: 3rem;
        --reader-tabs-top-fade-height: 1.75rem;

        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        min-width: 0;
        background: var(--reader-tabs-mobile-backdrop);
        color: var(--reader-tabs-mobile-tab-text, var(--reader-tabs-text));
        padding: 0 max(0.55rem, var(--safe-area-inset-right, 0rem)) 0
            max(0.55rem, var(--safe-area-inset-left, 0rem));
        pointer-events: auto;
    }

    .mobile-tab-strip::before {
        content: "";
        position: absolute;
        inset: 0 0 auto;
        border-top: 1px solid var(--reader-tabs-border);
        pointer-events: none;
        z-index: 0;
    }

    .mobile-tab-strip::after {
        content: "";
        position: absolute;
        right: 0;
        bottom: 100%;
        left: 0;
        height: var(--reader-tabs-top-fade-height);
        background: linear-gradient(
            180deg,
            transparent 0%,
            color-mix(in srgb, var(--background) 8%, transparent) 28%,
            color-mix(in srgb, var(--background) 24%, transparent) 52%,
            color-mix(in srgb, var(--background) 56%, transparent) 76%,
            color-mix(in srgb, var(--background) 82%, transparent) 90%,
            var(--background) 100%
        );
        pointer-events: none;
        z-index: 0;
    }

    .mobile-tab-scroller {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: start;
        gap: 0.14rem;
        min-width: 0;
        width: 100%;
        min-height: 3rem;
        overflow-x: auto;
        overflow-y: hidden;
        padding: 0 0.3rem;
        scrollbar-width: none;
        scroll-padding-inline: 2.7rem;
        touch-action: pan-x;
    }

    .mobile-tab-scroller::-webkit-scrollbar {
        display: none;
    }

    .tab-list-item {
        display: contents;
    }

    .utility-tab,
    .scroll-arrow {
        appearance: none;
        border: 0;
        box-shadow: none;
        background: transparent;
        color: inherit;
        cursor: pointer;
        font: inherit;
        letter-spacing: 0;
        text-transform: none;
    }

    .utility-tab {
        flex: 0 0 3.25rem;
        display: grid;
        position: relative;
        justify-items: center;
        align-content: center;
        gap: 0.1rem;
        height: 2.12rem;
        margin-top: 0.24rem;
        margin-bottom: 0.37rem;
        border: 1px solid var(--reader-tabs-mobile-utility-border);
        border-radius: 0.48rem;
        background: var(--reader-tabs-mobile-utility-bg);
        color: var(--reader-tabs-mobile-utility-text);
        padding: 0.24rem 0.28rem 0.3rem;
        transition:
            background-color 0.16s ease,
            border-color 0.16s ease,
            color 0.16s ease,
            transform 0.16s ease;
    }

    .utility-tab:hover {
        border-color: var(--reader-tabs-mobile-utility-hover-border);
        background: var(--reader-tabs-mobile-utility-hover-bg);
        color: var(--reader-tabs-mobile-utility-hover-text);
    }

    .utility-tab:active {
        transform: translateY(1px);
    }

    .utility-tab:focus-visible,
    .scroll-arrow:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
        z-index: 5;
    }

    .utility-tab.selected {
        border-color: var(--reader-tabs-mobile-utility-active-border);
        background: var(--reader-tabs-mobile-utility-active-bg);
        color: var(--reader-tabs-mobile-utility-active-text);
        z-index: 2;
    }

    .utility-tab :global(svg) {
        width: 0.9rem;
        height: 0.9rem;
    }

    .utility-tab span {
        min-width: 0;
        max-width: 100%;
        overflow: hidden;
        font-size: 0.61rem;
        font-weight: 780;
        line-height: 1;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .scroll-arrow {
        position: absolute;
        top: 1px;
        bottom: 0;
        z-index: 4;
        display: grid;
        place-items: center;
        width: var(--scroll-fade-width);
        opacity: 1;
        pointer-events: auto;
        transition: opacity 0.16s ease;
    }

    .scroll-arrow.hidden {
        opacity: 0;
        pointer-events: none;
    }

    .scroll-arrow.left {
        left: 0;
        background: linear-gradient(90deg, var(--reader-tabs-mobile-backdrop) 35%, transparent);
    }

    .scroll-arrow.right {
        right: 0;
        background: linear-gradient(270deg, var(--reader-tabs-mobile-backdrop) 35%, transparent);
    }

    .scroll-arrow :global(svg) {
        width: 0.86rem;
        height: 0.86rem;
        color: var(--reader-tabs-mobile-arrow-text);
    }

    @media only screen and (max-width: 360px) {
        .utility-tab {
            flex-basis: 2.7rem;
        }

        .utility-tab span {
            position: absolute;
            width: 1px;
            height: 1px;
            overflow: hidden;
            clip: rect(0 0 0 0);
            white-space: nowrap;
        }
    }
</style>
